import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { GraduationCap, Mail, Lock } from "lucide-react";

const BG =
  "https://customer-assets.emergentagent.com/job_schoolboss-platform/artifacts/ylmwhoyr_Hero%202.png";

export default function LoginPage() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [portalSlug, setPortalSlug] = useState(null);

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setPortalSlug(null);
    setLoading(true);
    const res = await login(email, password);
    setLoading(false);
    if (!res.ok) {
      setError(res.error);
      if (res.usePortal) {
        setPortalSlug(res.slug);
        toast.error("Please sign in through your school's portal.");
      } else {
        toast.error(res.error);
      }
      return;
    }
    toast.success(`Welcome back, ${res.user.name}!`);
    navigate(res.user.role === "super_admin" ? "/admin" : "/dashboard", { replace: true });
  };

  return (
    <div className="min-h-screen relative grid lg:grid-cols-2">
      {/* left brand panel */}
      <div className="hidden lg:flex relative overflow-hidden items-center justify-center p-12 bg-[#E5EEFF]">
        <img src={BG} alt="" className="absolute inset-0 w-full h-full object-cover" />
        <div className="relative max-w-sm text-center bg-white/70 backdrop-blur-sm rounded-3xl px-6 py-8 shadow-lg">
          <div className="w-16 h-16 mx-auto rounded-3xl bg-white flex items-center justify-center shadow-lg mb-6">
            <GraduationCap className="w-8 h-8 text-brand-purple" />
          </div>
          <h1 className="font-heading text-4xl font-semibold text-foreground">Welcome back, Boss!</h1>
          <p className="mt-4 text-brand-inkMuted">
            Log in to manage your schools, branches, and the wonderful little humans inside them.
          </p>
        </div>
      </div>

      {/* form */}
      <div className="flex items-center justify-center p-6 sm:p-12 bg-background relative">
        <div className="absolute top-6 left-6">
          <Link to="/" className="flex items-center" data-testid="login-back-home">
            <img
              src="/brand/logo-day.png"
              alt="SchoolBoss"
              className="h-10 w-auto select-none"
              draggable="false"
            />
          </Link>
        </div>

        <form onSubmit={onSubmit} className="w-full max-w-md space-y-6 mt-12" data-testid="login-form">
          <div className="text-center lg:text-left">
            <h2 className="font-heading text-3xl sm:text-4xl font-semibold text-foreground">Log in</h2>
            <p className="text-brand-inkMuted mt-2">
              New here?{" "}
              <Link to="/register" className="text-brand-purple font-semibold hover:underline" data-testid="login-go-register">
                Register your school
              </Link>
            </p>
          </div>

          <div className="space-y-4">
            <div>
              <Label htmlFor="email" className="font-heading text-foreground">Email</Label>
              <div className="relative mt-1.5">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-brand-inkMuted" />
                <Input
                  id="email"
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@school.com"
                  className="rounded-2xl h-12 pl-10 border-brand-purple/20 focus-visible:ring-brand-purple"
                  data-testid="login-email-input"
                />
              </div>
            </div>
            <div>
              <div className="flex items-center justify-between">
                <Label htmlFor="password" className="font-heading text-foreground">Password</Label>
                <Link to="/forgot-password" className="text-xs text-brand-purple hover:underline" data-testid="login-forgot-link">Forgot password?</Link>
              </div>
              <div className="relative mt-1.5">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-brand-inkMuted" />
                <Input
                  id="password"
                  type="password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  className="rounded-2xl h-12 pl-10 border-brand-purple/20 focus-visible:ring-brand-purple"
                  data-testid="login-password-input"
                />
              </div>
            </div>
          </div>

          {error && (
            <div className="rounded-2xl bg-[#FFEBEE] text-[#C62828] px-4 py-3 text-sm" data-testid="login-error">
              {error}
              {portalSlug && (
                <div className="mt-2">
                  <Link
                    to={`/school/${portalSlug}`}
                    className="inline-flex items-center gap-1 font-semibold text-[#C62828] hover:underline"
                    data-testid="login-portal-redirect"
                  >
                    Go to your school's portal →
                  </Link>
                </div>
              )}
            </div>
          )}

          <Button
            type="submit"
            disabled={loading}
            className="w-full h-12 rounded-full bg-brand-purple hover:bg-brand-purpleHover text-white shadow-sm"
            data-testid="login-submit-btn"
          >
            {loading ? "Logging in..." : "Log in"}
          </Button>

          <p className="text-xs text-brand-inkMuted text-center">
            Super Admin? Use the seeded credentials provided by your team.
          </p>
        </form>
      </div>
    </div>
  );
}
